import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { StatusContent } from '@/components/status/StatusContent';
import { useAuth } from '@/hooks/useAuth';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

/**
 * Status Page
 *
 * PUBLIC page (no auth required) — mirrors the `/design-system` pattern. This
 * is the stable public URL (`/status`) the marketing site footer links to for
 * system status + changelog.
 *
 * Logged-in users land on this same URL when they follow in-app "Status &
 * Feedback" links (navbar dropdown, Help page). For them we render the full
 * app shell (`<DashboardLayout>`) so they keep normal in-app navigation.
 * Anonymous visitors get a slim standalone top bar (logo + link back to the
 * marketing site) around the shared `<StatusContent />`, which owns all the
 * actual data fetching (public system-status + changelog endpoints, no auth)
 * and gates the feature-request form on `isAuthenticated` internally.
 *
 * Route: /status
 */

const MARKETING_SITE_URL = 'https://agile-assets.ai';

export const Status = () => {
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <DashboardLayout>
        <StatusContent />
      </DashboardLayout>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Standalone public chrome — no sidebar, no in-app nav */}
      <header className="bg-white border-b border-gray-200">
        <div className="px-6 h-16 flex items-center justify-between">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="h-12 w-auto object-contain"
          />
          <a
            href={MARKETING_SITE_URL}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Icon name={ICONS.ARROW_LEFT} size="xs" ariaHidden />
            {t('status_back_to_website')}
          </a>
        </div>
      </header>

      <main className="flex-1 px-6 pt-6 pb-12">
        <StatusContent />
      </main>
    </div>
  );
};
